import { useEffect, useRef } from 'react';
import { useGoogleOAuth } from '@react-oauth/google';

// The web "Sign in with Google" button (native apps use SocialAuthButtons). Replaces
// @react-oauth/google's <GoogleLogin>, which called google.accounts.id.initialize() again on every
// width change and every mount, and Google's script warns that only the last one counts.
// Google's sign-in is set up once per page load here; each mount only renders the button, and
// the credential goes to whichever button is mounted now.

const BUTTON = { type: 'standard', theme: 'outline', size: 'large' };
const BUTTON_HEIGHT = 40;

let nonce = null;
let initializedFor = null;
let current = null;

// One nonce per page load, sent to the server with the credential (routes/auth.js checks the ID
// token carries it). The token is minted with whatever nonce initialize() got, so it can't change
// per mount.
export function googleNonce() {
  if (!nonce) nonce = crypto.randomUUID();
  return nonce;
}

// The state above lives for the page load; tests start each case from a fresh page.
export function resetGoogleWebButtonForTests() {
  nonce = null;
  initializedFor = null;
  current = null;
}

function ensureInitialized(gsi, clientId) {
  if (initializedFor === clientId) return;
  gsi.initialize({
    client_id: clientId,
    nonce: googleNonce(),
    callback: (response) => {
      if (!response?.credential) {
        current?.onError?.();
        return;
      }
      current?.onSuccess({ credential: response.credential });
    },
  });
  initializedFor = clientId;
}

export default function GoogleWebButton({ onSuccess, onError, width }) {
  const ref = useRef(null);
  const { clientId, scriptLoadedSuccessfully } = useGoogleOAuth();
  const handlers = useRef({ onSuccess, onError });
  handlers.current = { onSuccess, onError };

  useEffect(() => {
    const target = {
      onSuccess: (r) => handlers.current.onSuccess(r),
      onError: () => handlers.current.onError?.(),
    };
    current = target;
    return () => { if (current === target) current = null; };
  }, []);

  useEffect(() => {
    const gsi = window.google?.accounts?.id;
    if (!scriptLoadedSuccessfully || !gsi || !ref.current) return;
    ensureInitialized(gsi, clientId);
    gsi.renderButton(ref.current, { ...BUTTON, width });
  }, [clientId, scriptLoadedSuccessfully, width]);

  return <div ref={ref} style={{ height: BUTTON_HEIGHT }} />;
}
